'use client';

import { Shield, Zap, CheckCircle, Settings, Terminal, BookOpen, Copy, Check, Brain } from 'lucide-react';
import { BRAND } from "@/lib/brand";
import Link from 'next/link';
import { useState } from 'react';

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      onClick={handleCopy}
      className="absolute top-3 right-3 p-1.5 rounded-md bg-gray-700 hover:bg-gray-600 transition-colors"
      title="Copy to clipboard"
    >
      {copied ? (
        <Check className="h-3.5 w-3.5 text-green-400" />
      ) : (
        <Copy className="h-3.5 w-3.5 text-gray-400" />
      )}
    </button>
  );
}

const claudeDesktopConfig = `{
  "mcpServers": {
    "designcommons": {
      "url": "https://trycommons.com/api/mcp/http",
      "headers": {
        "Authorization": "Bearer YOUR_API_KEY"
      }
    }
  }
}`;

const claudeCodeConfig = `claude mcp add designcommons \\
  --transport http \\
  --url https://trycommons.com/api/mcp/http \\
  --header "Authorization: Bearer YOUR_API_KEY"`;

export default function ClaudeIntegrationPage() {
  return (
    <div className="max-w-4xl mx-auto px-6 py-12">
      {/* Header */}
      <div className="text-center mb-12">
        <div className="inline-flex items-center justify-center w-20 h-20 bg-[#F5E6D3] rounded-2xl mb-6 shadow-sm">
          <svg className="w-12 h-12" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="m4.7144 15.9555 4.7174-2.6471.079-.2307-.079-.1275h-.2307l-.7893-.0486-2.6956-.0729-2.3375-.0971-2.2646-.1214-.5707-.1215-.5343-.7042.0546-.3522.4797-.3218.686.0608 1.5179.1032 2.2767.1578 1.6514.0972 2.4468.255h.3886l.0546-.1579-.1336-.0971-.1032-.0972L6.973 9.8356l-2.55-1.6879-1.3356-.9714-.7225-.4918-.3643-.4614-.1578-1.0078.6557-.7225.8803.0607.2246.0607.8925.686 1.9064 1.4754 2.4893 1.8336.3643.3035.1457-.1032.0182-.0728-.164-.2733-1.3539-2.4467-1.445-2.4893-.6435-1.032-.17-.6194c-.0607-.255-.1032-.4674-.1032-.7285L6.287.1335 6.6997 0l.9957.1336.419.3642.6192 1.4147 1.0018 2.2282 1.5543 3.0296.4553.8985.2429.8318.091.255h.1579v-.1457l.1275-1.706.2368-2.0947.2307-2.6957.0789-.7589.3764-.9107.7468-.4918.5828.2793.4797.686-.0668.4433-.2853 1.8517-.5586 2.9021-.3643 1.9429h.2125l.2429-.2429.9835-1.3053 1.6514-2.0643.7286-.8196.85-.9046.5464-.4311h1.0321l.759 1.1293-.34 1.1657-1.0625 1.3478-.8804 1.1414-1.2628 1.7-.7893 1.36.0729.1093.1882-.0183 2.8535-.607 1.5421-.2794 1.8396-.3157.8318.3886.091.3946-.3278.8075-1.967.4857-2.3072.4614-3.4364.8136-.0425.0304.0486.0607 1.5482.1457.6618.0364h1.621l3.0175.2247.7892.522.4736.6376-.079.4857-1.2142.6193-1.6393-.3886-3.825-.9107-1.3113-.3279h-.1822v.1093l1.0929 1.0686 2.0035 1.8092 2.5075 2.3314.1275.5768-.3218.4554-.34-.0486-2.2039-1.6575-.85-.7468-1.9246-1.621h-.1275v.17l.4432.6496 2.3436 3.5214.1214 1.0807-.17.3521-.6071.2125-.6679-.1214-1.3721-1.9246L14.38 17.959l-1.1414-1.9428-.1397.079-.674 7.2552-.3156.3703-.7286.2793-.6071-.4614-.3218-.7468.3218-1.4753.3886-1.9246.3157-1.53.2853-1.9004.17-.6314-.0121-.0425-.1397.0182-1.4328 1.9672-2.1796 2.9446-1.7243 1.8456-.4128.164-.7164-.3704.0667-.6618.4008-.5889 2.386-3.0357 1.4389-1.882.929-1.0868-.0062-.1579h-.0546l-6.3385 4.1164-1.1293.1457-.4857-.4554.0608-.7467.2307-.2429 1.9064-1.3114Z" fill="#D4A27F"/>
          </svg>
        </div>
        <h1 className="text-4xl font-bold text-gray-900 mb-4">Claude Integration</h1>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
          Connect {BRAND.name} to Claude Desktop, Claude Code, and any MCP-compatible AI assistant.
          Query your teams, projects, updates, and workflows using natural language.
        </p>
        <div className="flex items-center justify-center gap-2 mt-4">
          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200">
            <Terminal className="h-3 w-3 mr-1" />
            MCP Protocol
          </span>
          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200">
            26 tools available
          </span>
        </div>
      </div>

      {/* What You Can Do */}
      <div className="bg-white border border-gray-200 rounded-2xl p-8 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center gap-2">
          <Zap className="h-5 w-5 text-gray-700" />
          What you can do
        </h2>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="flex gap-3">
            <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-medium text-gray-900 mb-1">Query your teams & projects</h3>
              <p className="text-sm text-gray-600">Ask Claude about project statuses, team members, contributors, and timelines.</p>
            </div>
          </div>
          <div className="flex gap-3">
            <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-medium text-gray-900 mb-1">Browse updates & comments</h3>
              <p className="text-sm text-gray-600">Search and read updates, view comment threads, and track reactions across your org.</p>
            </div>
          </div>
          <div className="flex gap-3">
            <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-medium text-gray-900 mb-1">Review standups & retros</h3>
              <p className="text-sm text-gray-600">Get standup submissions, retro insights, action items, and team health summaries.</p>
            </div>
          </div>
          <div className="flex gap-3">
            <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-medium text-gray-900 mb-1">Generate reports & summaries</h3>
              <p className="text-sm text-gray-600">Use built-in prompts for weekly status reports, project briefings, and team health checks.</p>
            </div>
          </div>
          <div className="flex gap-3">
            <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-medium text-gray-900 mb-1">Post updates from Claude</h3>
              <p className="text-sm text-gray-600">Create updates and add comments directly from your AI assistant.</p>
            </div>
          </div>
          <div className="flex gap-3">
            <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-medium text-gray-900 mb-1">Explore design critiques</h3>
              <p className="text-sm text-gray-600">Review crit sessions, nominations, feedback, and design review history.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Example Prompts */}
      <div className="bg-gradient-to-b from-gray-50 to-white border border-gray-200 rounded-2xl p-8 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center gap-2">
          <BookOpen className="h-5 w-5 text-gray-700" />
          Things you can ask Claude
        </h2>
        <div className="grid gap-3">
          {[
            "What should I focus on today? Check my action items and what my team posted yesterday.",
            "Summarize last week's standup for the Platform team",
            "Who are the contributors on the Homepage Redesign project and what's the latest update?",
            "Draft a weekly rollup based on project updates from the past 7 days",
            "How is the Mobile team doing? Show me their recent retro and any open action items.",
            "Prep me for tomorrow's crit — what are we reviewing and what should I look for?",
            "Which projects have no updates in the past two weeks?",
            "What are all the pending action items from our last retro?",
          ].map((prompt, i) => (
            <div
              key={i}
              className="flex items-start gap-3 px-4 py-3 bg-white rounded-lg border border-gray-100"
            >
              <span className="text-gray-300 font-mono text-sm mt-0.5">&gt;</span>
              <span className="text-sm text-gray-700">{prompt}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Setup Instructions */}
      <div className="bg-white border border-gray-200 rounded-2xl p-8 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center gap-2">
          <Settings className="h-5 w-5 text-gray-700" />
          How to set up
        </h2>
        <ol className="space-y-6">
          <li className="flex gap-4">
            <div className="flex-shrink-0 w-8 h-8 bg-gray-100 text-gray-900 rounded-full flex items-center justify-center font-semibold text-sm">
              1
            </div>
            <div className="flex-1">
              <h3 className="font-medium text-gray-900 mb-1">Create an API key</h3>
              <p className="text-sm text-gray-600 mb-3">
                Go to your organization settings → <strong>API Keys</strong> and create a key with the <strong>&quot;MCP Server&quot;</strong> permission preset.
              </p>

              {/* Key type guidance */}
              <div className="grid gap-3">
                <div className="flex gap-3 p-3 bg-gray-50 rounded-lg border border-gray-100">
                  <div className="flex-shrink-0 w-8 h-8 bg-white border border-gray-200 rounded-lg flex items-center justify-center">
                    <svg className="w-4 h-4 text-gray-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Personal use → User Key <code className="px-1 py-0.5 bg-gray-100 rounded text-xs font-mono">dc_</code></p>
                    <p className="text-xs text-gray-500">For your own Claude Desktop or Claude Code. Updates and comments appear as <em>you</em>.</p>
                  </div>
                </div>
                <div className="flex gap-3 p-3 bg-gray-50 rounded-lg border border-gray-100">
                  <div className="flex-shrink-0 w-8 h-8 bg-white border border-gray-200 rounded-lg flex items-center justify-center">
                    <svg className="w-4 h-4 text-gray-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Shared / team use → Service Key <code className="px-1 py-0.5 bg-gray-100 rounded text-xs font-mono">dc_svc_</code></p>
                    <p className="text-xs text-gray-500">For bots, CI/CD, or shared automations. Actions appear as &quot;Service Bot.&quot;</p>
                  </div>
                </div>
              </div>
            </div>
          </li>
          <li className="flex gap-4">
            <div className="flex-shrink-0 w-8 h-8 bg-gray-100 text-gray-900 rounded-full flex items-center justify-center font-semibold text-sm">
              2
            </div>
            <div className="flex-1">
              <h3 className="font-medium text-gray-900 mb-2">Configure your client</h3>

              {/* Claude Desktop */}
              <div className="mb-4">
                <p className="text-sm font-medium text-gray-700 mb-2">Claude Desktop / Claude Enterprise</p>
                <div className="relative">
                  <pre className="text-xs bg-gray-900 text-gray-300 rounded-lg p-4 overflow-x-auto">
                    <code>{claudeDesktopConfig}</code>
                  </pre>
                  <CopyButton text={claudeDesktopConfig} />
                </div>
              </div>

              {/* Claude Code */}
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Claude Code (CLI)</p>
                <div className="relative">
                  <pre className="text-xs bg-gray-900 text-gray-300 rounded-lg p-4 overflow-x-auto">
                    <code>{claudeCodeConfig}</code>
                  </pre>
                  <CopyButton text={claudeCodeConfig} />
                </div>
              </div>
            </div>
          </li>
          <li className="flex gap-4">
            <div className="flex-shrink-0 w-8 h-8 bg-gray-100 text-gray-900 rounded-full flex items-center justify-center font-semibold text-sm">
              3
            </div>
            <div className="flex-1">
              <h3 className="font-medium text-gray-900 mb-1">Start asking questions</h3>
              <p className="text-sm text-gray-600">
                Open Claude and ask about your teams, projects, or updates. Claude will automatically use the {BRAND.name} tools to fetch the data.
              </p>
            </div>
          </li>
        </ol>
      </div>

      {/* How Claude Understands Your Data */}
      <div className="bg-gradient-to-b from-amber-50/50 to-white border border-amber-100 rounded-2xl p-8 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Brain className="h-5 w-5 text-gray-700" />
          How Claude understands your data
        </h2>
        <p className="text-sm text-gray-600 mb-4">
          Claude automatically reads a usage guide from your {BRAND.name} workspace when you connect.
          It knows how to navigate your data, resolve team and project names to IDs without asking you,
          and compose multi-step queries.
        </p>
        <ul className="space-y-2 text-sm text-gray-700">
          <li className="flex items-start gap-2">
            <span className="text-amber-500 mt-1">&#8226;</span>
            <span>Never need to say &quot;teamId xyz&quot; — just say &quot;Platform team&quot;</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-500 mt-1">&#8226;</span>
            <span>Ask in natural language</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-500 mt-1">&#8226;</span>
            <span>{BRAND.name} is the structured record — Claude goes there for team rituals, project status, and action items</span>
          </li>
        </ul>
      </div>

      {/* Available Tools */}
      <div className="bg-white border border-gray-200 rounded-2xl p-8 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-2 flex items-center gap-2">
          <Terminal className="h-5 w-5 text-gray-700" />
          Available tools
        </h2>
        <p className="text-sm text-gray-600 mb-6">
          26 tools across 7 categories, plus 5 browsable resources and 5 prompt templates.
        </p>

        <div className="grid gap-6">
          {[
            { category: 'Teams', tools: ['get_teams', 'get_team', 'get_team_members'] },
            { category: 'Projects', tools: ['get_projects', 'get_project', 'search_projects'] },
            { category: 'Updates & Comments', tools: ['get_updates', 'get_update', 'create_update', 'add_comment', 'get_comments', 'search_updates'] },
            { category: 'Standups', tools: ['get_standup_sessions', 'get_standup_session', 'get_standup_config'] },
            { category: 'Retros & Crits', tools: ['get_retro_sessions', 'get_retro_session', 'get_crit_sessions', 'get_crit_session'] },
            { category: 'Rollups', tools: ['get_rollups', 'get_rollup'] },
            { category: 'Organization', tools: ['get_members', 'get_member', 'search_members', 'get_action_items', 'get_activity_feed', 'get_org_summary'] },
          ].map(({ category, tools }) => (
            <div key={category}>
              <h3 className="text-sm font-semibold text-gray-900 mb-2">{category}</h3>
              <div className="flex flex-wrap gap-1.5">
                {tools.map(tool => (
                  <code
                    key={tool}
                    className="px-2 py-1 text-xs font-mono bg-gray-100 text-gray-700 rounded-md border border-gray-200"
                  >
                    {tool}
                  </code>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Security */}
      <div className="bg-white border border-gray-200 rounded-2xl p-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center gap-2">
          <Shield className="h-5 w-5 text-gray-700" />
          Security & permissions
        </h2>
        <ul className="space-y-2 text-sm text-gray-600">
          <li className="flex items-start gap-2">
            <span className="text-gray-400 mt-1">&#8226;</span>
            <span><strong>API key authentication</strong> &#8212; All requests require a valid API key. No passwords or OAuth tokens needed.</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-gray-400 mt-1">&#8226;</span>
            <span><strong>Scoped permissions</strong> &#8212; Each key only has access to the permissions you grant (read teams, read projects, write updates, etc.).</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-gray-400 mt-1">&#8226;</span>
            <span><strong>Two key types</strong> &#8212; User keys (<code className="px-1 py-0.5 bg-gray-100 rounded text-xs font-mono">dc_</code>) attribute actions to you. Service keys (<code className="px-1 py-0.5 bg-gray-100 rounded text-xs font-mono">dc_svc_</code>) attribute actions to &quot;Service Bot&quot; for shared automations.</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-gray-400 mt-1">&#8226;</span>
            <span><strong>Revocable anytime</strong> &#8212; Delete or rotate keys from your organization settings at any time.</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-gray-400 mt-1">&#8226;</span>
            <span><strong>HTTPS only</strong> &#8212; All data transmitted over TLS-encrypted connections.</span>
          </li>
        </ul>
        <p className="text-sm text-gray-500 mt-4">
          Need help? Check the <Link href="/docs/integrations" className="text-gray-900 underline hover:text-gray-700">integration docs</Link> or reach out to support.
        </p>
      </div>
    </div>
  );
}
